import { BankIcon, InternetIcon } from '@synergy/theme';
import { Divider, FormControlLabel, Grid, Switch, Theme } from '@mui/material';
import { Field, FieldProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloLinkRow } from '@synergy/components-palo';
import { PoolTextArea, PoolTextField, PoolTypography } from '@synergy/components';
import { makeStyles } from '@mui/styles';

import { ContactDetailOverviewAddress } from './contact-detail-overview-address';
import { ContactDetailOverviewBankAccounts } from './contact-detail-overview-bank-accounts';
import { EmailTable } from '../../../../components/emails/emails-table';
import { InternalNotes } from '../../../../../../common/components/internal-notes/internal-notes';
import { NoteType } from '../../../../../../common/models/enums/note-type';
import { PhonesTable } from '../../../../components/phones/phones-table';
import { SubtitleWithIcon } from '../../../../components/subtitle-with-icon';
import { contactSelector } from '../store/contact-detail-overview-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    divider: {
        margin: theme.spacing(4, 0),
        backgroundColor: '#DEDFE0',
    },
    basicInfos: {
        marginBottom: theme.spacing(4),
    },
}));

export const ContactDetailOverviewBasicInfos = () => {
    const classes = useStyles();
    const intl = useIntl();
    const contact = useAppSelector(contactSelector);

    return (
        <div>
            {/* Directly under the person, ahead of the basics: what someone
                wrote down about this contact is what the next caller needs to
                know before they read the address (#2193). */}
            <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.detail.internalNotes" />
            <Grid container={true} sx={{ mb: 4 }}>
                <Grid item={true} xs={12}>
                    <Field name="internalNotes">
                        {({ field }: FieldProps<string>) => (
                            <>
                                <PoolTextArea
                                    placeholder={intl.formatMessage({ id: 'app.label.description' })}
                                    rowsMax="50"
                                    rows="3"
                                    {...field}
                                />
                                <PaloLinkRow text={field.value} />
                            </>
                        )}
                    </Field>
                </Grid>
            </Grid>

            <PoolTypography styleType="sectionTitle" id="app.common.basicInfos" className={classes.basicInfos} />

            <ContactDetailOverviewAddress />

            <Divider className={classes.divider} />

            <EmailTable />

            <Divider className={classes.divider} />

            <SubtitleWithIcon title={<FormattedMessage id="app.common.website" />} icon={<InternetIcon />} />
            <Grid container={true}>
                <Grid item={true} xs={6}>
                    <Field name="website">
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                placeholder={intl.formatMessage({
                                    id: 'app.contactsAndOrganizations.detail.exampleWebsite',
                                })}
                                fullWidth={true}
                                error={!!meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                {...field}
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>

            <Divider className={classes.divider} />

            <PhonesTable withMobile={true} />

            <Divider className={classes.divider} />

            <SubtitleWithIcon title={<FormattedMessage id="app.common.bankData" />} icon={<BankIcon />} />
            <ContactDetailOverviewBankAccounts />

            <PoolTypography sx={{ mt: 4 }} styleType="sectionTitle" id="app.contactsAndOrganizations.detail.mails" />
            <Grid container={true}>
                <Grid item={true} xs={12}>
                    <Field name="receivesShopOrderMails">
                        {({ field, form }: FieldProps<boolean>) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={field.value ?? true}
                                        onChange={(_, checked) => form.setFieldValue(field.name, checked)}
                                    />
                                }
                                label={
                                    <FormattedMessage id="app.contactsAndOrganizations.detail.receivesShopOrderMails" />
                                }
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>

            <PoolTypography
                sx={{ mt: 4 }}
                styleType="sectionTitle"
                id="app.contactsAndOrganizations.detail.internalNotes.title"
            />
            <Grid container={true}>
                <Grid item={true} xs={12}>
                    <FormattedMessage id="app.contactsAndOrganizations.detail.internalNotes.text" />
                    {contact?.id && <InternalNotes noteType={NoteType.Contacts} entityId={contact.id} />}
                </Grid>
            </Grid>
        </div>
    );
};
